import { useEffect } from 'react';
import 'intersection-observer';

// NB. This ONLY supports images
// To lazy load images, add the `lazyload` prop to the `<Image />` component

// Get an image from the cache
const getImageFromCache = async (src) => {
  if (window.caches) {
    const cachedImage = await window.caches.match(src);

    if (cachedImage) {
      const imageBlob = await cachedImage.blob();
      return URL.createObjectURL(imageBlob);
    }
  }

  return null;
};

// Add a image to the cache
const addImageToCache = async (src) => {
  if (window.caches) {
    const cache = await window.caches.open('images');
    cache.add(src);
  }
};

// Load image, and add it to the cache if it's supported
const loadImage = async (entries, observer) => {
  for (const entry of entries) {
    // Check if the image intersects with the viewport
    if (entry.isIntersecting) {
      const image = entry.target;

      image.setAttribute('src', image.dataset.src);
      image.removeAttribute('data-src');

      observer.unobserve(image);

      addImageToCache(image.src);
    }
  }
};

// Attach an observer to the images passed
const observeImages = (images) => {
  images.map((image) => imageObserver.observe(image));
};

// Setup an observer with some settings
const imageObserver = new IntersectionObserver(loadImage, { threshold: 0.2 });

// Get an array of images to be lazy loaded
const getImagesToLazyLoad = () => {
  const images = [];

  const domImages = document.getElementsByTagName('img');
  for (const image of domImages) {
    if (image.hasAttribute('data-src')) images.push(image);
  }

  return images;
};

const useLazyLoad = (dependencies) => {
  // This effect runs on every render, optimize if this is a problem
  // Pass in optional array of dependencies to optimize performance
  useEffect(() => {
    // Wait for the page to render
    requestAnimationFrame(async () => {
      const images = getImagesToLazyLoad();

      // Return if no images to load
      if (!images) return;

      // Lazy load images if cache is not supported
      if (!window.caches) return observeImages(images);

      // Check the cache for each image
      await Promise.all(
        images.map(async (image) => {
          const cachedImage = await getImageFromCache(image.dataset.src);

          // If image from the cache is valid, then set the image
          if (cachedImage) {
            image.setAttribute('src', cachedImage);
            image.removeAttribute('data-src');
          }
        })
      );

      // Lazy load images not found in the cache
      // Calls getImagesToLazyLoad to get a fresh list of images
      // without all the cached images
      return observeImages(getImagesToLazyLoad());
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
};

export default useLazyLoad;
